<template>
  <SmartLinkItem v-if="data" :item-id="data.system.id">
    <SeoMetatags/>
    <component :is="activeComponent" v-if="activeComponent" />
  </SmartLinkItem>
</template>

<script setup lang="ts">
import { HomePage, InteriorPage, ComingSoonPage, FaqPage, DirectoryLandingPage } from '#components'
import { contentTypes } from '~/models'
import type { AccordionListState } from '~/types/AccordionListState'

const siteStore = useSiteStore()
const pageStore = usePageStore()
const homepageStore = useHomePageStore()
const route = useRoute()
const config = useRuntimeConfig()
const { locale } = useI18n()

let itemId = ''
if (route.query && !config.public.isProduction) {
  if (route.query.itemid) {
    itemId = useSanitize(route.query.itemid as string, 'text')
  }
}

const hasHeroOverlay = useState<boolean>('hasHeroOverlay')
hasHeroOverlay.value = false

const showGlobalContentBlockState = useState<boolean>('showGlobalContentBlockState')
showGlobalContentBlockState.value = true

const initState = { data: [] }
const accordionListState = useState<AccordionListState>('AccordionListState', () => initState)

const sanitizedPath = useSanitize(route.path, 'text')

const redirectPath = siteStore.getRedirectPath(sanitizedPath)
if (redirectPath) {
  navigateTo(redirectPath, { external: redirectPath.includes('http') })
}

let pageRouteInfo = siteStore.getPage(sanitizedPath, itemId, siteStore.collection)

//check for default english route if localized route not found
if (!pageRouteInfo && locale.value !== siteStore.defaultLanguage) {
  const defaultRoutePath = sanitizedPath.replace('/' + locale.value, '')
  pageRouteInfo = siteStore.getPage(defaultRoutePath, itemId, siteStore.collection)
}
const { homepageItem } = useNavigationGroup()

if (homepageItem.value && homepageItem.value.system?.id && homepageStore.page?.system?.id !== homepageItem.value.system?.id) {
  await useAsyncData(`page:${homepageItem.value.system?.id}`, async () => {
    await homepageStore.fetchData(homepageItem!.value!.system?.id, false, locale.value)
  })
}

const { data, error } = await useAsyncData(`page:${pageRouteInfo?.itemid}`, async () => {
  let pageData = null
  if (pageRouteInfo) {
    await pageStore.fetchData(pageRouteInfo.itemid, false, locale.value)
    pageData = pageStore.page
  }
  return pageData
})
if (error.value) {
  console.error(error.value)
}

if (!data.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found'
  })
}


const activeComponent = resolveComponent(data.value?.system?.type)

function resolveComponent(type: string) {
  switch (type) {
    case contentTypes.homepage.codename:
      return HomePage
    case contentTypes.interior_page.codename:
      return InteriorPage
    case contentTypes.frequently_asked_questions.codename:
      return FaqPage
    case contentTypes.site_redirect_page.codename:
      return ComingSoonPage
    case contentTypes.directory_landing_page.codename: {
      const showHeaderAndFooter = useState('showHeaderAndFooter')
      showHeaderAndFooter.value = false
      return DirectoryLandingPage
    }
    default:
      return null
  }
}
</script>
